(function() {
  'use strict';

  const controllers = new WeakMap();

  const REPRESENTATIVE_ADDRESSES = Object.freeze({
    SG: { zip: '238801' },
    US: { province: 'NY', zip: '10001' },
    GB: { province: 'ENG', zip: 'SW1A 1AA' },
    AU: { province: 'NSW', zip: '2000' },
    CA: { province: 'ON', zip: 'M5V 3A8' },
    AE: { province: 'DU', zip: '00000' },
    IN: { province: 'MH', zip: '400001' },
    SA: { zip: '11564' },
    NZ: { province: 'AUK', zip: '1010' },
    MX: { province: 'DF', zip: '06000' },
    ID: { province: 'JK', zip: '10110' },
    PH: { province: 'PH-00', zip: '1000' },
    TH: { province: 'TH-10', zip: '10100' },
    VN: { zip: '100000' },
    MY: { province: 'KUL', zip: '50000' },
    TW: { zip: '100' },
    JP: { province: 'JP-13', zip: '100-0001' },
    KR: { province: 'KR-11', zip: '04524' },
    CN: { province: 'BJ', zip: '100000' },
    HR: { zip: '10000' },
    CZ: { zip: '11000' },
    DK: { zip: '1050' },
    FR: { zip: '75001' },
    DE: { zip: '10115' },
    CY: { zip: '1010' },
    MC: { zip: '98000' },
    BE: { zip: '1000' },
    CH: { zip: '8001' },
    FI: { zip: '00100' },
    PL: { zip: '00-001' },
    AT: { zip: '1010' },
    NL: { zip: '1012 JS' },
    SE: { zip: '111 20' },
    IE: { province: 'D', zip: 'D02 X285' },
    IT: { province: 'RM', zip: '00100' },
    ES: { province: 'M', zip: '28001' },
    NO: { zip: '0150' },
    BQ: { zip: '0000 AA' },
    CR: { province: 'CR-SJ', zip: '10101' },
    DO: { zip: '10101' },
    LB: { zip: '1107 2800' },
    AR: { province: 'C', zip: 'C1000' },
    BO: { zip: '0000' },
    BR: { province: 'SP', zip: '01001-000' },
    CL: { province: 'RM', zip: '8320000' },
    CO: { province: 'DC', zip: '110111' },
    EC: { zip: '170150' },
    FK: { zip: 'FIQQ 1ZZ' },
    GF: { zip: '97300' },
    GY: { zip: '00000' },
    PY: { zip: '1209' },
    PE: { province: 'PE-LMA', zip: '15001' },
    GS: { zip: 'SIQQ 1ZZ' },
    SR: { zip: '00000' },
    UY: { province: 'UY-MO', zip: '11000' },
    VE: { province: 'VE-A', zip: '1010' }
  });

  const SINGAPORE_NATIVE_RATES = Object.freeze([
    { maxGrams: 1000, price: 5 },
    { minGrams: 1001, price: 10 }
  ]);

  function normalizeRoot() {
    const routeRoot = window.Shopify && window.Shopify.routes && window.Shopify.routes.root
      ? window.Shopify.routes.root
      : '/';
    return routeRoot.endsWith('/') ? routeRoot : routeRoot + '/';
  }

  function wait(ms, signal) {
    return new Promise(function(resolve, reject) {
      const timer = window.setTimeout(resolve, ms);
      if (!signal) return;
      if (signal.aborted) {
        window.clearTimeout(timer);
        reject(new DOMException('Aborted', 'AbortError'));
        return;
      }
      signal.addEventListener('abort', function() {
        window.clearTimeout(timer);
        reject(new DOMException('Aborted', 'AbortError'));
      }, { once: true });
    });
  }

  function readJson(response) {
    return response.text().then(function(text) {
      if (!text || !text.trim()) return null;
      try { return JSON.parse(text); } catch (error) { return null; }
    });
  }

  function buildAddressParams(address) {
    const params = new URLSearchParams();
    params.set('shipping_address[country]', address.country);
    if (address.province) params.set('shipping_address[province]', address.province);
    if (address.zip) params.set('shipping_address[zip]', address.zip);
    return params;
  }

  function buildAddressCandidates(countryCode, countryName) {
    const normalizedCode = String(countryCode || '').trim().toUpperCase();
    const normalizedName = String(countryName || '').trim();
    const representative = REPRESENTATIVE_ADDRESSES[normalizedCode] || null;
    const candidates = [];
    const seen = new Set();

    function add(address) {
      if (!address || !address.country) return;
      const key = [address.country, address.province || '', address.zip || ''].join('|');
      if (seen.has(key)) return;
      seen.add(key);
      candidates.push(address);
    }

    if (normalizedCode && representative) add(Object.assign({ country: normalizedCode }, representative));
    if (normalizedCode) add({ country: normalizedCode });
    if (normalizedName && normalizedName.toUpperCase() !== normalizedCode) {
      if (representative) add(Object.assign({ country: normalizedName }, representative));
      add({ country: normalizedName });
    }
    return candidates;
  }

  function currencyFractionDigits(currency) {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency', currency: currency
      }).resolvedOptions().maximumFractionDigits;
    } catch (error) { return 2; }
  }

  function roundCurrency(amount, currency) {
    const numericAmount = Number(amount);
    if (!Number.isFinite(numericAmount)) return null;
    const digits = currencyFractionDigits(currency);
    const factor = Math.pow(10, digits);
    return Math.round((numericAmount + Number.EPSILON) * factor) / factor;
  }

  function formatMoneyAmount(amount, currency) {
    const numericAmount = Number(amount);
    if (!Number.isFinite(numericAmount)) return '';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency', currency: currency, currencyDisplay: 'code'
      }).format(numericAmount).replace(/\u00a0/g, ' ');
    } catch (error) {
      return currency + ' ' + numericAmount.toFixed(2);
    }
  }

  function readPresentmentRate(shopCurrency, presentmentCurrency) {
    if (!shopCurrency || !presentmentCurrency) return null;
    if (shopCurrency === presentmentCurrency) return 1;
    const currencyState = window.Shopify && window.Shopify.currency ? window.Shopify.currency : null;
    const activeCurrency = currencyState ? String(currencyState.active || '').trim().toUpperCase() : '';
    const rate = currencyState ? Number(currencyState.rate) : NaN;
    if (activeCurrency === presentmentCurrency && Number.isFinite(rate) && rate > 0) return rate;
    return null;
  }

  function rateInPresentmentCurrency(rate, shopCurrency, presentmentCurrency) {
    const rawPrice = Number(rate && rate.price);
    if (!Number.isFinite(rawPrice) || rawPrice < 0) return null;
    const rateCurrency = String(rate && rate.currency ? rate.currency : shopCurrency).trim().toUpperCase();
    if (!rateCurrency || rateCurrency === presentmentCurrency) {
      return roundCurrency(rawPrice, presentmentCurrency);
    }
    if (rateCurrency === shopCurrency) {
      const presentmentRate = readPresentmentRate(shopCurrency, presentmentCurrency);
      if (presentmentRate !== null) {
        return roundCurrency(rawPrice * presentmentRate, presentmentCurrency);
      }
    }
    return null;
  }

  function singaporeConfiguredRate(cart) {
    const grams = Number(cart && cart.total_weight);
    if (!Number.isFinite(grams) || grams < 0) return null;
    for (const tier of SINGAPORE_NATIVE_RATES) {
      const aboveMinimum = tier.minGrams === undefined || grams >= tier.minGrams;
      const belowMaximum = tier.maxGrams === undefined || grams <= tier.maxGrams;
      if (aboveMinimum && belowMaximum) return tier.price;
    }
    return null;
  }

  function createController(element) {
    const valueElement = element.querySelector('[data-bijon-shipping-value]');
    const totalRow = element.nextElementSibling && element.nextElementSibling.matches('[data-bijon-estimated-total]')
      ? element.nextElementSibling : null;
    const totalValueElement = totalRow ? totalRow.querySelector('[data-bijon-estimated-total-value]') : null;
    let requestSequence = 0;
    let requestController = null;

    const labels = {
      calculating: element.dataset.labelCalculating || 'Calculating…',
      free: element.dataset.labelFree || 'Free',
      from: element.dataset.labelFrom || 'From',
      checkout: element.dataset.labelCheckout || 'Final at checkout',
      notRequired: element.dataset.labelNotRequired || 'Not required'
    };

    function normalizeSummaryOrder() {
      const parent = element.parentElement;
      if (!parent) return;
      const subtotalRow = parent.querySelector('.cart-drawer-total-row, .cart-total-row');
      if (subtotalRow && subtotalRow.nextElementSibling !== element) parent.insertBefore(subtotalRow, element);
      if (totalRow && element.nextElementSibling !== totalRow) parent.insertBefore(totalRow, element.nextSibling);
    }

    function setValue(text, state) {
      if (!valueElement) return;
      valueElement.textContent = text;
      valueElement.dataset.state = state || '';
    }

    function setEstimatedTotal(text, state) {
      if (!totalValueElement) return;
      totalValueElement.textContent = text;
      totalValueElement.dataset.state = state || '';
    }

    function itemSubtotalCents(cart) {
      const apiSubtotal = Number(cart && cart.items_subtotal_price);
      if (Number.isFinite(apiSubtotal) && apiSubtotal >= 0) return apiSubtotal;

      if (cart && Array.isArray(cart.items)) {
        return cart.items.reduce(function(sum, item) {
          const linePrice = Number(item && item.final_line_price);
          return sum + (Number.isFinite(linePrice) && linePrice >= 0 ? linePrice : 0);
        }, 0);
      }

      return null;
    }

    function merchandiseTotalCents(cart) {
      const subtotalCents = itemSubtotalCents(cart);
      const cartTotalCents = Number(cart && cart.total_price);

      if (Number.isFinite(cartTotalCents) && cartTotalCents >= 0) {
        if (!Number.isFinite(subtotalCents) || cartTotalCents <= subtotalCents) {
          return cartTotalCents;
        }
      }

      return Number.isFinite(subtotalCents) ? subtotalCents : null;
    }

    function setEstimatedTotalFromAmounts(cart, shippingAmount, currency) {
      const subtotalCents = merchandiseTotalCents(cart);
      if (!Number.isFinite(subtotalCents)) {
        setEstimatedTotal(labels.checkout, 'fallback');
        return;
      }
      const subtotal = subtotalCents / 100;
      const roundedShipping = roundCurrency(Number(shippingAmount || 0), currency);
      const total = roundCurrency(subtotal + Number(roundedShipping || 0), currency);
      const formatted = formatMoneyAmount(total, currency);
      setEstimatedTotal(formatted || labels.checkout, formatted ? 'ready' : 'fallback');
    }

    function setFallback() {
      setValue(labels.checkout, 'fallback');
      setEstimatedTotal(labels.checkout, 'fallback');
    }

    async function pollAsyncRates(params, signal, sequence) {
      const root = normalizeRoot();
      const url = root + 'cart/async_shipping_rates.json?' + params.toString();
      for (let attempt = 0; attempt < 8; attempt += 1) {
        if (attempt > 0) await wait(attempt < 3 ? 300 : 550, signal);
        if (sequence !== requestSequence) return null;
        const response = await fetch(url, {
          method: 'GET', headers: { Accept: 'application/json' }, credentials: 'same-origin',
          cache: 'no-store', signal: signal
        });
        if (response.status === 202) continue;
        if (!response.ok) throw new Error('Shipping rate polling failed with ' + response.status);
        const payload = await readJson(response);
        if (payload === null) continue;
        return Array.isArray(payload.shipping_rates) ? payload.shipping_rates : [];
      }
      return null;
    }

    async function directShippingRates(params, signal) {
      const response = await fetch(normalizeRoot() + 'cart/shipping_rates.json?' + params.toString(), {
        method: 'GET', headers: { Accept: 'application/json' }, credentials: 'same-origin',
        cache: 'no-store', signal: signal
      });
      if (!response.ok) throw new Error('Direct shipping rate request failed with ' + response.status);
      const payload = await readJson(response);
      return payload && Array.isArray(payload.shipping_rates) ? payload.shipping_rates : [];
    }

    async function ratesForAddress(address, signal, sequence) {
      const root = normalizeRoot();
      const params = buildAddressParams(address);
      const prepareResponse = await fetch(root + 'cart/prepare_shipping_rates.json?' + params.toString(), {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        credentials: 'same-origin', cache: 'no-store', signal: signal
      });
      if (!prepareResponse.ok && prepareResponse.status !== 202) {
        throw new Error('Shipping rate preparation failed with ' + prepareResponse.status);
      }
      const asyncRates = await pollAsyncRates(params, signal, sequence);
      if (asyncRates !== null) return asyncRates;
      return directShippingRates(params, signal);
    }

    async function fetchCurrentCart(signal) {
      const response = await fetch(normalizeRoot() + 'cart.js', {
        method: 'GET', headers: { Accept: 'application/json' }, credentials: 'same-origin',
        cache: 'no-store', signal: signal
      });
      if (!response.ok) throw new Error('Cart total request failed with ' + response.status);
      const hydratedCart = await readJson(response);
      return hydratedCart && typeof hydratedCart === 'object' ? hydratedCart : null;
    }

    async function fetchRates(signal, sequence) {
      const countryCode = String(element.dataset.countryCode || '').trim();
      const countryName = String(element.dataset.countryName || '').trim();
      const candidates = buildAddressCandidates(countryCode, countryName);
      let lastError = null;
      for (const address of candidates) {
        try {
          const rates = await ratesForAddress(address, signal, sequence);
          if (rates.length) return rates;
        } catch (error) {
          if (error && error.name === 'AbortError') throw error;
          lastError = error;
        }
      }
      if (lastError) throw lastError;
      return [];
    }

    function renderRates(rates, cart, currency) {
      const countryCode = String(element.dataset.countryCode || '').trim().toUpperCase();
      const shopCurrency = String(element.dataset.shopCurrency || '').trim().toUpperCase();
      const sourceRates = Array.isArray(rates) ? rates : [];
      const convertedRates = sourceRates.map(function(rate) {
        return { rate: rate, price: rateInPresentmentCurrency(rate, shopCurrency, currency) };
      }).filter(function(entry) {
        return Number.isFinite(entry.price) && entry.price >= 0;
      }).sort(function(a, b) { return a.price - b.price; });

      if (!convertedRates.length) {
        setFallback();
        return;
      }

      const freeRate = convertedRates.find(function(entry) { return entry.price === 0; });
      if (freeRate) {
        setValue(labels.free, 'ready');
        setEstimatedTotalFromAmounts(cart, 0, currency);
        return;
      }

      if (countryCode === 'SG' && currency === 'SGD') {
        const nativeSingaporeRate = singaporeConfiguredRate(cart);
        if (nativeSingaporeRate !== null) {
          setValue(formatMoneyAmount(nativeSingaporeRate, currency), 'ready');
          setEstimatedTotalFromAmounts(cart, nativeSingaporeRate, currency);
          return;
        }
      }

      const lowest = convertedRates[0].price;
      setEstimatedTotalFromAmounts(cart, lowest, currency);
      const formatted = formatMoneyAmount(lowest, currency);
      if (!formatted) {
        setFallback();
        return;
      }
      const distinctPrices = new Set(convertedRates.map(function(entry) {
        return String(roundCurrency(entry.price, currency));
      }));
      setValue(distinctPrices.size > 1 ? labels.from + ' ' + formatted : formatted, 'ready');
    }

    async function refresh(cart) {
      normalizeSummaryOrder();
      requestSequence += 1;
      const sequence = requestSequence;
      if (requestController) requestController.abort();
      requestController = new AbortController();

      if (!cart || !cart.item_count) {
        element.hidden = true;
        if (totalRow) totalRow.hidden = true;
        setValue(labels.calculating, 'idle');
        setEstimatedTotal(labels.calculating, 'idle');
        return;
      }

      element.hidden = false;
      if (totalRow) totalRow.hidden = false;
      setValue(labels.calculating, 'loading');
      setEstimatedTotal(labels.calculating, 'loading');

      try {
        const currentCart = await fetchCurrentCart(requestController.signal);
        const hydratedCart = currentCart || cart;
        if (sequence !== requestSequence) return;
        const currency = String(hydratedCart.currency || element.dataset.currency || '').trim().toUpperCase();

        if (hydratedCart.requires_shipping === false) {
          setValue(labels.notRequired, 'ready');
          setEstimatedTotalFromAmounts(hydratedCart, 0, currency);
          return;
        }

        const rates = await fetchRates(requestController.signal, sequence);
        if (sequence !== requestSequence) return;
        renderRates(rates, hydratedCart, currency);
      } catch (error) {
        if (error && error.name === 'AbortError') return;
        if (sequence !== requestSequence) return;
        setFallback();
      }
    }

    normalizeSummaryOrder();
    return { refresh: refresh };
  }

  window.BijonCartShipping = {
    refresh: function(target, cart) {
      const element = typeof target === 'string' ? document.querySelector(target) : target;
      if (!element) return Promise.resolve();
      let controller = controllers.get(element);
      if (!controller) {
        controller = createController(element);
        controllers.set(element, controller);
      }
      return controller.refresh(cart);
    }
  };
})();
